<template>
  <StepModel
    :ui="uiBaseStep"
    :name="STEP_NAMES.additionalInformation"
    ui-title="Pre-fill your application (optional)"
    ui-subtitle="We need to collect your signature on our application form when we meet you.
    If you answer these last few questions, we will bring a pre-filled application form when
    we meet you and all you need to do is signing it! If you don’t have the time now, you can
    always skip this step and fill it when we meet you."
    ui-error-message="Please review the fields marked in red"
    ui-secondary-button="Skip"
    :ui-secondary-action="goToNextStepWithoutValidate"
  >
    <FieldsetModel
      :ui="uiBaseFieldset"
      :name="FIELDSET_NAMES.personalInformation"
      ui-title="Personal information"
    >
      <UiWrapper :name="FIELD_NAMES.passportNumber" label="Passport number" optional>
        <InputText />
      </UiWrapper>
      <UiWrapper :name="FIELD_NAMES.educationalLevel" label="Level of education" optional>
        <Dropdown
          placeholder="Level of education"
          :options="[
            {
              label: 'Post Graduate',
              value: 'Post Graduate',
            },
            {
              label: 'Graduate',
              value: 'Graduate',
            },
            {
              label: 'High School',
              value: 'High School',
            },
            {
              label: 'Primary School',
              value: 'Primary School',
            }
          ]"
        />
      </UiWrapper>
      <UiWrapper :name="FIELD_NAMES.salaryPaymentDay" label="Salary payment day" optional>
        <Dropdown placeholder="Salary payment day" :options="monthlyInstalmentOptions" />
      </UiWrapper>
    </FieldsetModel>

    <FieldsetModel
      :ui="uiBaseFieldset"
      :name="FIELDSET_NAMES.residentialAddress"
      ui-title="Residential Address"
      ui-columns="2"
    >
      <UiWrapper :name="FIELD_NAMES.residentialAddress" label="Address line 1" optional>
        <InputText data-forced-fullwidth="true" />
      </UiWrapper>
      <UiWrapper :name="FIELD_NAMES.residentialAdditionalAddress" label="Address line 2" optional>
        <InputText />
      </UiWrapper>
      <UiWrapper :name="FIELD_NAMES.residentialNearestLandmark" label="Nearest landmard" optional>
        <InputText />
      </UiWrapper>
      <UiWrapper :name="FIELD_NAMES.streetName" label="Area/street name" optional>
        <InputText data-forced-fullwidth="true" />
      </UiWrapper>
      <UiWrapper :name="FIELD_NAMES.residentialEmirate" label="Emirate" optional>
        <Dropdown placeholder="Emirate" :options="EMIRATE_LIST" />
      </UiWrapper>
      <UiWrapper :name="FIELD_NAMES.poBox" label="PO Box" optional>
        <InputText />
      </UiWrapper>
    </FieldsetModel>
    <FieldsetModel
      :ui="uiBaseFieldset"
      :name="FIELDSET_NAMES.homeCountryInformation"
      ui-title="Home country information"
      ui-columns="2"
    >
      <UiWrapper :name="FIELD_NAMES.homeCountryAddress" label="Address line 1" optional>
        <InputText data-forced-fullwidth="true" />
      </UiWrapper>
      <UiWrapper :name="FIELD_NAMES.homeCountryAdditionalAddress" label="Address line 2" optional>
        <InputText />
      </UiWrapper>
      <UiWrapper :name="FIELD_NAMES.homeCountryNumber" label="State" optional>
        <InputText />
      </UiWrapper>

      <UiWrapper :name="FIELD_NAMES.homeCountryPoscode" label="Post Code / Zip Code" optional>
        <InputText />
      </UiWrapper>
      <UiWrapper :name="FIELD_NAMES.homeCountryCountry" label="Country" optional>
        <Suggest
          :exact-match="true"
          placeholder="Country"
          :get-formatted-item="formatMatchedString"
          :get-suggestions="getCountries"
        />
      </UiWrapper>
      <UiWrapper :name="FIELD_NAMES.homeCountryPhoneCountryCode" label="Country code" optional>
        <Dropdown placeholder="Country code" :options="MAPPED_PHONE_CODE" />
      </UiWrapper>
      <UiWrapper :name="FIELD_NAMES.homeCountryPhone" label="Phone number" optional>
        <InputText />
      </UiWrapper>
    </FieldsetModel>

    <FieldsetModel
      :ui="uiBaseFieldset"
      :name="FIELDSET_NAMES.references"
      ui-title="Reference in UAE"
    >
      <UiWrapper :name="FIELD_NAMES.referencesFullName" label="Full name of reference" optional>
        <InputText />
      </UiWrapper>
      <UiWrapper :name="FIELD_NAMES.referencesPhone" label="(+971) Contact's phone number" optional>
        <InputText type="tel" prefix="+971" :maxlength="9" />
      </UiWrapper>
    </FieldsetModel>

    <FieldsetModel
      :ui="uiBaseFieldset"
      :name="FIELDSET_NAMES.referencesHomeCountry"
      ui-title="References in Home Country"
      ui-columns="2"
    >
      <UiWrapper
        :name="FIELD_NAMES.referencesHomeCountryFullName"
        label="Contact full name"
        optional
      >
        <InputText data-forced-fullwidth="true" />
      </UiWrapper>
      <UiWrapper :name="FIELD_NAMES.referencesHomeCountryPhoneCode" label="Country code" optional>
        <Dropdown placeholder="Country code" :options="MAPPED_PHONE_CODE" />
      </UiWrapper>
      <UiWrapper
        :name="FIELD_NAMES.referencesHomeCountryPhone"
        label="Contact's phone number"
        optional
      >
        <InputText />
      </UiWrapper>
    </FieldsetModel>

    <FieldsetModel
      :ui="uiBaseFieldset"
      :name="FIELDSET_NAMES.bank"
      ui-title="Bank information"
      ui-subtitle="You can set up a direct debit instruction to make your monthly
      credit card payments from this account. We now only need this information to
      pre-fill the Direct Debit Authority form for you."
      ui-columns="2"
    >
      <UiWrapper :name="FIELD_NAMES.emiratesNBD" optional>
        <Suggest
          data-forced-fullwidth="true"
          placeholder="Bank name"
          :exact-match="true"
          :get-formatted-item="formatMatchedString"
          :get-suggestions="getInstitutions"
        />
      </UiWrapper>
      <UiWrapper :name="FIELD_NAMES.IBANNumber" label="Your IBAN number" optional>
        <InputText type="tel" />
      </UiWrapper>
      <UiWrapper
        :name="FIELD_NAMES.ddaPaymentAmount"
        :label="`Your monthly instalment amount is\n ${getMonthlyInstalment}`"
        :readOnly="true"
      >
        <InputText data-forced-fullwidth="true" />
      </UiWrapper>
      <UiWrapper
        :name="FIELD_NAMES.bankAccountTitle"
        label="Name of the account holder"
        :readOnly="true"
      >
        <InputText data-forced-fullwidth="true" :readOnly="true" />
      </UiWrapper>
    </FieldsetModel>
  </StepModel>
</template>


<script>
// Models
import StepModel from "@/lib/components/models/step";
import FieldsetModel from "@/lib/components/models/fieldset";

// Ui Wrappers
import UiWrapper from "@/forms/components/base/ui-wrapper";
import FieldIncome from "@/forms/components/shared/fields/income";

// Ui Components
import { InputText, Dropdown, Suggest } from "@dunia/ui";

// Utils
import * as conditions from "@/forms/conditions";
import FormComponentMixin from "@/lib/mixins/form-component-mixin";
//import getGoogleTagManager from "@/forms/helpers/google-tag-manager";
export default {
  name: "AdditionalInformation",

  mixins: [FormComponentMixin],

  components: {
    StepModel,
    FieldsetModel,
    InputText,
    Dropdown,
    UiWrapper,
    Suggest,
    FieldIncome
  },
  data: () => ({
    MONTHLYINSTALMENT: "myid",
    message: "Welcome"
  }),
  mounted() {
    //getGoogleTagManager();
    var gtagID = process.env.VUE_APP_GA_MEASUREMENT_ID;
    if (gtagID) {
      (function(w, d, s, l, i) {
        w[l] = w[l] || [];
        w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
        var f = d.getElementsByTagName(s)[0],
          j = d.createElement(s),
          dl = l != "dataLayer" ? "&l=" + l : "";
        j.async = true;
        j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
        f.parentNode.insertBefore(j, f);
      })(window, document, "script", "dataLayer", `${gtagID}`);
    }
  },
  created() {
    // var vueInstance = document.getElementById('myId') ;
    // console.log("element="+vueInstance.innerHTML);
  },
  computed: {},

  methods: {
    ...conditions
  }
};
</script>
